import { gregorianToJalali } from '../../utils/jalali';
import { formatDurationHMS } from '../../utils/comparison/computeComparisonDeltas';
import { formatWorkoutDistanceM } from '../../utils/workoutStream';

export type WorkoutRowMetricSource = {
  total_time?: number | null;
  total_time_planned?: number | string | null;
  tss_actual?: number | null;
  tss_planned?: number | string | null;
  distance?: number | null;
};

/** Calendar day-row metrics — duration · TSS · distance (mobile order). */
export function buildWorkoutRowMetrics(workout: WorkoutRowMetricSource): string[] {
  const actualTime = workout.total_time != null ? Number(workout.total_time) : null;
  const plannedTime =
    workout.total_time_planned != null && workout.total_time_planned !== ''
      ? Number(workout.total_time_planned)
      : null;
  const displayTime = actualTime ?? plannedTime;

  const actualTss = workout.tss_actual != null ? Number(workout.tss_actual) : null;
  const plannedTss =
    workout.tss_planned != null && workout.tss_planned !== '' ? Number(workout.tss_planned) : null;
  const displayTss = actualTss ?? plannedTss;

  const metrics: string[] = [];
  if (displayTime != null && Number.isFinite(displayTime) && displayTime > 0) {
    metrics.push(formatDurationHMS(displayTime));
  }
  if (displayTss != null && Number.isFinite(displayTss)) {
    metrics.push(`${Math.round(displayTss)} TSS`);
  }
  if (workout.distance != null) {
    const meters = Number(workout.distance);
    if (Number.isFinite(meters) && meters > 0) {
      metrics.push(formatWorkoutDistanceM(meters));
    }
  }
  return metrics;
}

export function getTodayMetricValue(today: Record<string, unknown> | null | undefined, keys: string[]): number {
  if (!today) return 0;
  for (const key of keys) {
    const rawValue = today[key];
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
      const numericValue = Number(rawValue);
      return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
    }
  }
  return 0;
}

export type ReadinessTone = 'rest' | 'coast' | 'build' | 'prime';

/** Maps readiness score to i18n label key + status chip variant (Readiness.html). */
export function getReadinessStatus(value: number): {
  labelKey: string;
  tone: ReadinessTone;
  chipClass: string;
} {
  if (value < 25) {
    return {
      labelKey: 'calendar.readiness.status.rest',
      tone: 'rest',
      chipClass: 'rd__statuschip--rest',
    };
  }
  if (value < 50) {
    return {
      labelKey: 'calendar.readiness.status.coast',
      tone: 'coast',
      chipClass: 'rd__statuschip--coast',
    };
  }
  if (value < 75) {
    return {
      labelKey: 'calendar.readiness.status.build',
      tone: 'build',
      chipClass: '',
    };
  }
  return {
    labelKey: 'calendar.readiness.status.prime',
    tone: 'prime',
    chipClass: 'rd__statuschip--prime',
  };
}

export function getWorkoutTssTotal(workouts: { tss_actual?: number | null; tss_planned?: number | string | null }[] = []) {
  return workouts.reduce((sum, workout) => {
    const actualTss = workout?.tss_actual != null ? Number(workout.tss_actual) : null;
    const plannedTss =
      workout?.tss_planned != null && workout?.tss_planned !== ''
        ? Number(workout.tss_planned)
        : null;
    return sum + (actualTss ?? plannedTss ?? 0);
  }, 0);
}

export function getWorkoutColorSelect(workout: {
  is_created_by_athlete?: boolean;
  total_time?: number | null;
  total_time_planned?: number | string | null;
  tss_actual?: number | null;
  tss_planned?: number | string | null;
}): string {
  if (workout?.is_created_by_athlete) return '#9ca3af';

  const tt = workout?.total_time != null ? Number(workout.total_time) : null;
  const pt =
    workout?.total_time_planned != null && workout.total_time_planned !== ''
      ? Number(workout.total_time_planned)
      : null;

  if (tt && pt) {
    if (pt * 0.7 <= tt && tt < pt * 1.3) return '#16a34a';
    if (pt * 0.35 <= tt && tt < pt * 1.65) return '#f97316';
    return '#ef4444';
  }

  const atss = workout?.tss_actual != null ? Number(workout.tss_actual) : null;
  const ptss =
    workout?.tss_planned != null && workout.tss_planned !== ''
      ? Number(workout.tss_planned)
      : null;

  if (ptss && atss) {
    if (ptss * 0.7 <= atss && atss < ptss * 1.3) return '#16a34a';
    if (ptss * 0.35 <= atss && atss < ptss * 1.65) return '#f97316';
    return '#ef4444';
  }

  return '#9ca3af';
}

/** Workouts on a calendar grid day (safe array). */
export function getDayWorkouts(item?: { extra?: { workouts?: unknown } } | null) {
  const ws = item?.extra?.workouts;
  return Array.isArray(ws) ? ws : [];
}

/** Week-strip dot color — planned pink, done uses completion color from getWorkoutColorSelect. */
export function getWeekStripPipColor(
  workout: {
    is_created_by_athlete?: boolean;
    total_time?: number | null;
    total_time_planned?: number | string | null;
    tss_actual?: number | null;
    tss_planned?: number | string | null;
  },
  selected: boolean,
): string {
  const done = workout.total_time != null || workout.tss_actual != null;
  if (!done) {
    return selected ? 'rgba(255,255,255,0.92)' : '#ea4782';
  }

  const base = getWorkoutColorSelect(workout);
  if (!selected) return base;

  switch (base) {
    case '#16a34a':
      return '#bbf7d0';
    case '#f97316':
      return '#fed7aa';
    case '#ef4444':
      return '#fecaca';
    default:
      return '#bbf7d0';
  }
}

export function getSingleUploadedWorkoutDate(uploadResponse: unknown) {
  const res = uploadResponse as {
    data?: { imports?: unknown[]; data?: { imports?: unknown[] } };
    imports?: unknown[];
  };
  const imports = res?.data?.imports ?? res?.imports ?? res?.data?.data?.imports ?? [];
  const uploadedImport = Array.isArray(imports) ? (imports[0] as Record<string, unknown>) : null;
  const startTime = Number(uploadedImport?.start_time);

  const toJalaliTarget = (workoutDate: Date) => {
    const j = gregorianToJalali(workoutDate.getFullYear(), workoutDate.getMonth() + 1, workoutDate.getDate());
    return {
      dateKey: `${j.year}/${j.month}/${j.dat}`,
      selectedDate: `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.dat).padStart(2, '0')}`,
    };
  };

  if (Number.isFinite(startTime) && startTime > 0) {
    return toJalaliTarget(new Date(startTime > 1000000000000 ? startTime : startTime * 1000));
  }

  const filename = String(uploadedImport?.filename ?? uploadedImport?.normalized_filename ?? '');
  const filenameDate = filename.match(/(20\d{2})[-_](\d{2})[-_](\d{2})/);
  if (filenameDate) {
    return toJalaliTarget(
      new Date(Number(filenameDate[1]), Number(filenameDate[2]) - 1, Number(filenameDate[3])),
    );
  }

  return null;
}
